import { ReactNode, useState } from "react";
import Addicon from "@mui/icons-material/Add";
import TimeLineIcon from "remixicon-react/TimeLineIcon";
import TruckLineIcon from "remixicon-react/TruckLineIcon";
import { useHistory, useLocation } from "react-router-dom";
import { createUseStyles } from "react-jss";
import EdbPopin from "$containers/EDB/EdbPopin";
import { publicIconPath } from "$utils/constants";
import { getEdbStatusColor, getEdbStatusLabel } from "$utils/typeConstants";
import { DispositionType, EdbStatus, PreparationOrderStatus } from "$types";
import { Text, Button, Table, InfoContainer, Badge } from "$components";
import { EMPTY_DATA } from "$containers/constants";
import useAuth from "$hooks/useAuth";
import { ImportButtonWithModal } from "$components/Forms/ImportButtonWithModal";
import { ImportType } from "$types/import/ImportType";
import { theme } from "$utils/theme/bataillog";

type Props = {
  dockyard: any;
  edbs: any[];
  handleEdbRedirect: () => void;
  dockyardId: string;
};

function EDBs({ dockyard, edbs, handleEdbRedirect, dockyardId }: Props) {
  const { isAdmin, isContributor, isClient } = useAuth();
  const history = useHistory();
  const location = useLocation<{
    breadcrumbs?: Array<{ path: string; name: string }>;
  }>();
  const classes = useStyles();

  const [edbPopin, setEdbPopin] = useState<any>(null);
  const [indexEdb, setIndexEdb] = useState(0);

  const buildEdbBreadcrumbs = (edb: any) => {
    const existing = location.state?.breadcrumbs;
    if (existing?.length) {
      return [...existing, { path: `/edbs/${edb._id}`, name: edb.edbId }];
    }
    const { pathname } = location;
    const parts = pathname.split("/").filter(Boolean);
    const chain: Array<{ path: string; name: string }> = [];
    if (pathname.startsWith("/chantiers/")) {
      chain.push({ path: "/chantiers", name: "Liste des chantiers" });
      if (dockyard?.name) chain.push({ path: pathname, name: dockyard.name });
    } else if (pathname.startsWith("/projets/")) {
      chain.push({ path: "/projets", name: "Liste des projets" });
      const idProject = parts[1];
      const projectName = dockyard?.project?.name || dockyard?.project?.code;
      if (idProject && projectName) {
        chain.push({ path: `/projets/${idProject}`, name: projectName });
      }
      if (dockyard?.name) chain.push({ path: pathname, name: dockyard.name });
    }
    chain.push({ path: `/edbs/${edb._id}`, name: edb.edbId });
    return chain;
  };

  const onEdbClickAction = (edb: any) => {
    if (edb.status === EdbStatus.CART_CREATED) {
      history.push(`/create-edb/${edb._id}`);
    } else {
      history.push(`/edbs/${edb._id}`, {
        breadcrumbs: buildEdbBreadcrumbs(edb),
      });
    }
  };

  const resetEdbPopin = () => {
    setEdbPopin(null);
  };

  const tableBody = edbs.map((edb: any, currentEdb: number) => {
    const renderDispatched = (ops: any[]) => {
      const dispatched = ops?.filter(
        (op: any) =>
          op.status === PreparationOrderStatus.SHIPPED ||
          op.status === PreparationOrderStatus.DELIVERED ||
          (op.status === PreparationOrderStatus.COLLECTED &&
            op.shipType !== DispositionType.COLLECTED)
      );
      return dispatched?.length;
    };

    const renderCell = (
      value: ReactNode,
      customOnClickAction?: () => void
    ) => ({
      isDisable:
        edb.isDisable ||
        edb.dockyardDisable ||
        edb.projectDisable ||
        edb.companyDisable ||
        false,
      onClickAction:
        customOnClickAction ||
        (() => {
          onEdbClickAction(edb);
        }),
      value,
    });

    const isEdbDisabled =
      edb.isDisable ||
      edb.dockyardDisable ||
      edb.projectDisable ||
      edb.companyDisable;

    return [
      renderCell(
        <div className={isEdbDisabled ? classes.cellContentDisabled : ""}>
          <Text type="important">{edb.edbId || ""}</Text>
          <br />
          <Text style={{ width: 150 }}>{edb.company?.name || ""}</Text>
        </div>
      ),
      renderCell(
        <div className={isEdbDisabled ? classes.cellContentDisabled : ""}>
          <Text type="important">{edb?.project?.code || ""}</Text>
          <br />
          <Text style={{ width: 150 }}>{edb?.project?.name || ""}</Text>
        </div>
      ),
      renderCell(
        <span className={classes.text}>{`${edb.products?.length} ${
          edb.products?.length > 1 ? "produits" : "produit"
        }`}</span>
      ),
      renderCell(
        <span className={classes.text}>{`${edb?.ops?.length || 0} ${
          edb?.ops?.length > 1 ? "préparations" : "préparation"
        }`}</span>
      ),
      renderCell(
        <span className={classes.text}>{`${
          edb?.ops ? renderDispatched(edb?.ops) : 0
        } ${
          renderDispatched(edb?.ops) > 1 ? "expéditions" : "expédition"
        }`}</span>
      ),
      renderCell(
        <span>
          {edb.ops?.length
            ? edb.ops?.map((op: any) => (
                <div>
                  <Text type="important">{op?.reference}</Text>
                  <br />
                  <Text>{op?.refPreparationEntrepot || EMPTY_DATA}</Text>
                </div>
              ))
            : "Pas de référence"}
        </span>
      ),
      renderCell(
        <div style={{ whiteSpace: "nowrap", margin: "4px 0" }}>
          {edb.status === EdbStatus.CART_CREATED ? (
            <Button
              variant="outlined"
              onClick={() => {}}
              style={{
                padding: "7px",
                fontSize: 13,
                minWidth: 130,
                display: "block",
                lineHeight: "15px",
              }}
            >
              Brouillon
            </Button>
          ) : edb.preparationType === "Préparation & restockage" ? (
            <Button
              onClick={(event) => {
                event.stopPropagation();
                setEdbPopin(edb);
                setIndexEdb(currentEdb);
              }}
              style={{
                padding: "7px",
                fontSize: 13,
                minWidth: 130,
                display: "block",
                lineHeight: "15px",
              }}
            >
              Donner <br />
              les instructions
            </Button>
          ) : (
            <Badge
              colorManual={getEdbStatusColor(edb.status, theme, edb.isDisable)}
            >
              <span
                style={{
                  width: 124,
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  display: "inline-block",
                  overflow: "hidden",
                  fontFamily: "Roboto",
                  fontSize: 13,
                  textAlign: "center",
                  fontWeight: 500,
                }}
              >
                {getEdbStatusLabel(edb.status)}
              </span>
            </Badge>
          )}
        </div>,
        () => {
          if (
            edb.preparationType !== "Préparation & restockage" ||
            edb.status !== "prep_avalaible"
          ) {
            onEdbClickAction(edb);
          }
        }
      ),
      renderCell(
        <span className={classes.text}>{edb.isDisable ? "OUI" : "NON"}</span>
      ),
    ];
  });

  const tableHead = [
    { id: "edbNumber", label: "Identification", minWidth: 150 },
    { id: "project", label: "Projet", minWidth: 150 },
    { id: "qty", label: "Produit", minWidth: 100 },
    {
      id: "qtyPrep",
      label: (
        <div>
          <TimeLineIcon
            color={theme.color.blue}
            size={15}
            style={{ marginRight: 5 }}
          />
          Préparation
        </div>
      ),
      minWidth: 150,
    },
    {
      id: "qtyExpedition",
      label: (
        <div>
          <TruckLineIcon
            color={theme.color.blue}
            size={15}
            style={{ marginRight: 5 }}
          />
          Expédition
        </div>
      ),
      minWidth: 150,
    },
    {
      id: "refPreparationEntrepot",
      label: "Ref OP, prep. entrepôt",
      minWidth: 150,
    },
    { id: "status", label: "Statut", minWidth: 100 },
    { id: "annule", label: "Annulé", minWidth: 80 },
  ];

  const disableAddEDB =
    dockyard?.isDisable ||
    dockyard?.projectDisable ||
    dockyard?.companyDisable ||
    dockyard?.status === "done" ||
    (!isAdmin() && !isClient() && !isContributor());

  return (
    <>
      <InfoContainer
        bodyClassName={classes.EdbBodyTable}
        className={classes.edbContainer}
        leftHeader={
          <div className={classes.infoHeader}>
            <img alt="ico-batail" src={`${publicIconPath}/casque-black.svg`} />
            <Text type="h3">Expressions de besoin</Text>
          </div>
        }
        rightHeader={
          <>
            <Button
              className={disableAddEDB ? classes.CtaDone : ""}
              onClick={handleEdbRedirect}
              variant="outlined"
              disabled={disableAddEDB}
            >
              <Addicon />
              Ajouter une EDB
            </Button>
            {!isContributor() && (
              <ImportButtonWithModal
                disabled={disableAddEDB}
                title="Opérations en masse"
                dataName="EDB du chantier"
                importType={ImportType.DOCKYARD_EDB}
                buttonTexts={{
                  importButtonText: "Importer des EDB",
                  updateButtonText: "Mettre à jour des EDB",
                }}
                contextId={dockyardId}
                isFeminine
              />
            )}
          </>
        }
      >
        <Table
          rowsPerPage={8}
          thead={tableHead}
          bodyData={tableBody}
          bodyMissingLabelText={
            !edbs.length
              ? "Aucune expression de besoin n'a encore été ajoutée à ce chantier"
              : undefined
          }
          usePaper={false}
        />
      </InfoContainer>
      <EdbPopin edb={edbPopin} resetEdb={resetEdbPopin} indexEdb={indexEdb} />
    </>
  );
}

export default EDBs;

const useStyles = createUseStyles(() => ({
  EdbBodyTable: {
    padding: 0,
  },
  text: {
    marginLeft: 5,
  },
  cellContentDisabled: {
    color: "#B3BAC5 !important",
    "& *": {
      color: "#B3BAC5 !important",
    },
  },
  infoHeader: {
    display: "flex",
    alignItems: "center",
    "& h3": {
      margin: "0 8px",
    },
  },

  infoContainer: {
    marginTop: 10,
    marginBottom: 20,
  },
  CtaDone: {
    color: `${theme.color.gray3} !important`,
    pointerEvents: "none",
    "&:hover": {
      color: `${theme.color.gray3} !important`,
    },
  },
  edbContainer: {
    marginTop: 10,
  },
}));
